import { Injectable } from '@angular/core';
import { AngularFirestore, AngularFirestoreDocument, AngularFirestoreCollection } from '@angular/fire/firestore';
import { Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';

type DocPredicate < T > = string | AngularFirestoreDocument < T > ;
type CollectionPredicate < T > = string | AngularFirestoreCollection < T > ;

@Injectable({
  providedIn: 'root'
})

export class DatabaseService {
    constructor(
        private afstore: AngularFirestore) {
    }

    /**
     * Initiate the get to collection documents process
     * @param path is collection name
     * @param query is optional referring to collection filters
     */
    getDocuments(path: string, query ?: any): Observable < any > {
        return this.afstore
        .collection(path, query)
        .snapshotChanges()
        .pipe(
            map(actions => {
            return actions.map(a => {
                // tslint:disable-next-line: ban-types
                const data: Object = a.payload.doc.data();
                const id = a.payload.doc.id;
                return {
                id,
                ...data
                };
            });
            })
        )
        .pipe(shareReplay(1));
    }

    /**
     * Initiate the get to user by id
     * @param collection is collection name
     * @param id is collection name
     */
    getDocument(path: any): Observable < any >  {
        return this.afstore.doc(path)
            .snapshotChanges()
            .pipe(
                map(doc => {
                    return {
                        id: doc.payload.id,
                        ...doc.payload.data
                    };
                })
            )
            .pipe(shareReplay(1));
    }

    async getDocumentById(collection: string, docId: string) {
        const docRef = this.afstore.collection(collection).doc(docId);

        return (await docRef.ref.get()).data();
    }

    /**
     * Initiate the get to document by id
     */
    doc < T >(ref: DocPredicate < T > ): AngularFirestoreDocument < T > {
        return typeof ref === 'string' ? this.afstore.doc < T > (ref) : ref;
    }

    /**
     * Create Id for document
     */
    createId(): string {
        return this.afstore.createId();
    }

    /**
     * Update document by Id and data
     */
    update < T > (ref: DocPredicate < T > , data: any): Promise < void > {
        return this.doc(ref).update({
        ...data
        });
    }

    add < T >(ref: CollectionPredicate < T > , data): Promise < firebase.default.firestore.DocumentReference > {
        return this.collection(ref).add({
        ...data
        });
    }

    collection < T >(ref: CollectionPredicate < T > , queryFn ? ): AngularFirestoreCollection < T > {
        return typeof ref === 'string' ? this.afstore.collection < T > (ref, queryFn) : ref;
    }
}
